import React from 'react';
import { Typography, Divider, Row, Col, Slider, Button, Switch, Space, Radio } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { API_HOST, fixUrl } from '../../../services/api';
// El MISMO uploader que usa el catalogo de genetica para su logo, su hero y su
// imagen de historia: sube y persiste en una llamada, con quitar, preview sobre
// damero y los mismos topes. Dos uploaders para la misma accion serian dos
// comportamientos que divergen.
import SubirImagen from './catalogo/SubirImagen';
// Cómo se leen la genealogía y los DEP's: la MISMA definición que usa el
// asistente de PDF (`utils/vistasFicha.js`). Lo que el productor elige acá es
// además el arranque del asistente para carátulas y catálogo.
import { GENEALOGIA_VISTA_INFO, DEPS_VISTA_INFO } from '../../../utils/vistasFicha';

const { Title, Text } = Typography;

// ImageOption vive a NIVEL DE MÓDULO (no dentro de EstructuraTab). Si se define
// adentro, cada onChange —incluido arrastrar el slider del logo— re-renderiza el
// padre y React ve una función NUEVA → remonta estas imágenes de preview
// (parpadean/recargan en cada movimiento). Misma razón por la que ColorField
// vive afuera en ColoresTab: no definir componentes durante el render.
const ImageOption = ({ value, currentValue, src, alt, onClick }) => {
  const isSelected = value === currentValue;
    return (
      <div 
        onClick={() => onClick(value)}
        style={{
          position: 'relative',
          cursor: 'pointer',
          border: isSelected ? '3px solid #1890ff' : '2px solid #e8e8e8',
          borderRadius: 8,
          padding: 4,
          transition: 'all 0.3s',
          backgroundColor: isSelected ? '#e6f7ff' : '#fff',
        }}
      >
        <img 
          src={src} 
          alt={alt}
          style={{ 
            width: '100%', 
            height: 'auto',
            borderRadius: 4,
            display: 'block'
          }}
        />
        {isSelected && (
          <CheckCircleFilled 
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              fontSize: 20,
              color: '#1890ff',
              backgroundColor: '#fff',
              borderRadius: '50%'
            }}
          />
        )}
      </div>
    );
};

const ALTURA_LOGO_DEFAULT = 44;   // default real del header (maxHeight 44)

// El slider de alto de un logo. A nivel de módulo por lo mismo que
// ImageOption. `valor` null = "sin elegir": se muestra `heredado` y el botón
// Restaurar no aparece.
const TamanoLogo = ({ titulo, ayuda, valor, heredado, onCambiar }) => {
  const actual = valor || heredado;
  return (
    <div>
      {titulo && <Text strong style={{ display: 'block', marginBottom: 4 }}>{titulo}</Text>}
      <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>{ayuda}</Text>
      <Row gutter={[16, 8]} align="middle">
        <Col flex="auto">
          <Slider min={24} max={96} step={2} value={actual} onChange={onCambiar} />
        </Col>
        <Col flex="none">
          <Text strong>{actual} px</Text>
          {valor && (
            <Button type="link" size="small" style={{ padding: '0 0 0 8px' }} onClick={() => onCambiar(null)}>
              Restaurar
            </Button>
          )}
        </Col>
      </Row>
    </div>
  );
};

// Cómo se ve el iso como favicon: una pestaña de navegador de mentira. El
// navegador lo dibuja a 16px y decide él el tamaño, por eso al lado del iso
// no hay slider (decisión del usuario, 2026-10-14).
const VistaFavicon = ({ iso }) => (
  <div>
    <Text strong style={{ display: 'block', marginBottom: 8 }}>Así se ve en la pestaña</Text>
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%',
      padding: '8px 14px', background: '#f0f0f0', borderRadius: '8px 8px 0 0',
      border: '1px solid #d9d9d9', borderBottom: 'none',
    }}>
      {iso
        ? <img src={fixUrl(iso)} alt="" style={{ width: 16, height: 16, objectFit: 'contain', flex: 'none' }} />
        : <span style={{ width: 16, height: 16, borderRadius: 3, background: '#d9d9d9', flex: 'none' }} />}
      <Text style={{ fontSize: 12 }} ellipsis>Micrositio del remate</Text>
    </div>
    <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
      {iso ? '' : 'Sin iso, la pestaña muestra el ícono de Puja. '}
      Se ve cuando el micrositio se abre directo. Incrustado en tu sitio web, la pestaña muestra el ícono de tu sitio.
    </Text>
  </div>
);

/**
 * @param {string[]} ocultar  Bloques que NO se dibujan. Existe porque este tab
 *   se reusa en el panel del catalogo de genetica, que no tiene barra de
 *   filtros lateral: su filtro vive DENTRO de la seccion de hembras, y
 *   `filtro_posicion` esta excluido a proposito de `DEFAULT_CATALOGO_CONFIG`.
 *   Sin esto el productor configuraria una posicion que no existe, apretaria
 *   guardar, y el backend descartaria la clave en silencio.
 *   Mismo motivo para `genealogia_vista`/`deps_vista`: son de la ficha del
 *   REMATE (`AnimalPage.jsx`); el catalogo de genetica tiene su propia
 *   presentacion fija (barra de percentil) y no las lee.
 *
 *   Default `[]`: el micrositio de remate, que ya esta en produccion, sigue
 *   dibujando todo igual sin tocar una linea de su llamada.
 *
 * @param {object}   [imagenes]       `{ logo, mobile, iso, fallback }`: las URLs
 *   actuales del logo principal, del logo mobile, del iso (favicon) y de la
 *   imagen para animales sin foto.
 * @param {function} [onSubirImagen]  (file, campo) => Promise. `campo` es el
 *   nombre del archivo que espera `PUT /eventos/:id`: `img_evento`,
 *   `logo_mobile`, `logo_iso` o `img_fallback_animal`.
 * @param {function} [onQuitarImagen] (campo) => Promise.
 *
 *   LAS TRES SON OPCIONALES, y los dos bloques de imagenes se dibujan solo si
 *   viene `onSubirImagen`. Hasta el 2026-10-06 estas imagenes se cargaban en el
 *   FORMULARIO DEL EVENTO, con nombres que no decian donde iban ("Imagen
 *   Principal" era el logo de la cabecera, "Imagen Secundaria" el fondo del
 *   hero) y lejos de todo lo demas que configura el micrositio. El usuario lo
 *   noto armando el demo: "es donde tenemos todos los parametros del
 *   micrositio". Se mudaron aca, que es donde ya estaba el TAMAÑO del logo.
 *   El catalogo de genetica no las pasa: tiene las suyas en su propio tab,
 *   porque alla viven en el JSON de config y no en columnas del evento.
 */
const EstructuraTab = ({
  config, onChange, ocultar = [], imagenes = {}, onSubirImagen, onQuitarImagen,
}) => {
  const conImagenes = Boolean(onSubirImagen);
  return (
    <div className="estructura-tab">
      {/* CABECERAS */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 8 }}>Tipo de Cabecera</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Selecciona el diseño del encabezado de tu micrositio
        </Text>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <ImageOption
              value="cabecera-1"
              currentValue={config.cabecera}
              src={`${API_HOST}/static/uploads/cabeceras/cabecera-1.webp`}
              alt="Logo izquierda + menú centrado"
              onClick={(v) => onChange('cabecera', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Logo izquierda + menú centrado
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <ImageOption
              value="cabecera-2"
              currentValue={config.cabecera}
              src={`${API_HOST}/static/uploads/cabeceras/cabecera-2.webp`}
              alt="Logo centrado + menú integrado"
              onClick={(v) => onChange('cabecera', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Logo centrado + menú integrado
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <ImageOption
              value="cabecera-3"
              currentValue={config.cabecera}
              src={`${API_HOST}/static/uploads/cabeceras/cabecera-3.webp`}
              alt="Logo centrado + menú debajo"
              onClick={(v) => onChange('cabecera', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Logo centrado + menú debajo
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <ImageOption
              value="cabecera-4"
              currentValue={config.cabecera}
              src={`${API_HOST}/static/uploads/cabeceras/cabecera-4.webp`}
              alt="Logo izquierda + menú debajo"
              onClick={(v) => onChange('cabecera', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Logo izquierda + menú debajo
            </Text>
          </Col>
        </Row>
      </div>

      <Divider />

      {/* LOS TRES LOGOS DEL REMATE (2026-10-14), cada uno con su control al
          lado: principal + su tamaño, mobile + su tamaño, iso + cómo se ve
          como favicon. A dos columnas desde md: desde que la vista previa pasó
          a ser un modal (f6da40c) la config ocupa todo el ancho, y lo que
          antes se pisaba en ~250px ahora entra. En pantallas angostas se
          apilan. Los tres se pueden elegir también como imagen de la
          cabecera del PDF (Generar PDF → Cabecera). */}
      {conImagenes ? (
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 16 }}>Logos</Title>
          <Row gutter={[32, 28]}>
            <Col xs={24} md={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Logo principal</Text>
              <SubirImagen
                valor={imagenes.logo}
                campo="img_evento"
                onSubir={onSubirImagen}
                onQuitar={onQuitarImagen}
                alto={56}
                ayuda="El de la cabecera en computadora. PNG con fondo transparente es lo que mejor queda."
              />
            </Col>
            <Col xs={24} md={12}>
              <TamanoLogo
                titulo="Tamaño del logo principal"
                ayuda="Qué tan alto se ve en la cabecera del micrositio en computadora."
                valor={config.logo_altura}
                heredado={ALTURA_LOGO_DEFAULT}
                onCambiar={(v) => onChange('logo_altura', v)}
              />
            </Col>

            <Col xs={24} md={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Logo mobile</Text>
              <SubirImagen
                valor={imagenes.mobile}
                campo="logo_mobile"
                onSubir={onSubirImagen}
                onQuitar={onQuitarImagen}
                alto={56}
                ayuda="El de la cabecera en el celular. Conviene uno compacto: uno ancho empuja el menú fuera de pantalla. Si no cargás uno, se usa el principal."
              />
            </Col>
            <Col xs={24} md={12}>
              {/* Sin tamaño propio sigue al del principal: así ningún
                  micrositio cambió de aspecto al separar los dos. */}
              <TamanoLogo
                titulo="Tamaño del logo mobile"
                ayuda="Qué tan alto se ve en el celular. Si no lo tocás, usa el del principal."
                valor={config.logo_mobile_altura}
                heredado={config.logo_altura || ALTURA_LOGO_DEFAULT}
                onCambiar={(v) => onChange('logo_mobile_altura', v)}
              />
            </Col>

            <Col xs={24} md={12}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Iso</Text>
              <SubirImagen
                valor={imagenes.iso}
                campo="logo_iso"
                onSubir={onSubirImagen}
                onQuitar={onQuitarImagen}
                alto={56}
                ayuda="Solo el ícono, sin texto, cuadrado. Es el ícono de la pestaña del navegador (favicon)."
              />
            </Col>
            <Col xs={24} md={12}>
              <VistaFavicon iso={imagenes.iso} />
            </Col>
          </Row>
        </div>
      ) : (
        <div style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 8 }}>Tamaño del Logo</Title>
          <TamanoLogo
            titulo=""
            ayuda="Ajustá qué tan grande se ve el logo en la cabecera del micrositio"
            valor={config.logo_altura}
            heredado={ALTURA_LOGO_DEFAULT}
            onCambiar={(v) => onChange('logo_altura', v)}
          />
        </div>
      )}

      <Divider />

      {/* COLUMNAS CATÁLOGO */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 8 }}>Columnas del Catálogo</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Cantidad de columnas para mostrar los animales
        </Text>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <ImageOption
              value="3"
              currentValue={config.columnas_catalogo}
              src={`${API_HOST}/static/uploads/estructura/tres-columns.webp`}
              alt="3 columnas"
              onClick={(v) => onChange('columnas_catalogo', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              3 Columnas
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <ImageOption
              value="4"
              currentValue={config.columnas_catalogo}
              src={`${API_HOST}/static/uploads/estructura/cuatro-columns.webp`}
              alt="4 columnas"
              onClick={(v) => onChange('columnas_catalogo', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              4 Columnas
            </Text>
          </Col>
        </Row>
      </div>

      <Divider />

      {/* ANIMALES SIN FOTO. Va pegado a las columnas del catalogo porque es
          sobre las mismas tarjetas: que se ve en la que no tiene foto. El
          switch es una clave de config (viaja con Guardar); la imagen se sube
          en el acto, como las de arriba. */}
      {conImagenes && (
        <>
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>Animales sin foto</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              La imagen que ocupa el lugar de la foto cuando un animal no tiene ninguna.
            </Text>
            <SubirImagen
              valor={imagenes.fallback}
              campo="img_fallback_animal"
              onSubir={onSubirImagen}
              onQuitar={onQuitarImagen}
              alto={72}
              ayuda="Una foto genérica de tu cabaña o tu marca. Se recorta como una foto de animal."
            />
            {/* El switch con su etiqueta en una fila que puede envolver, y la
                explicacion debajo a todo el ancho: en la columna angosta, el
                texto al costado del switch se partia en silabas. */}
            <Space align="center" wrap style={{ marginTop: 20 }}>
              <Switch
                checked={Boolean(config.fallback_reemplaza_video)}
                onChange={(v) => onChange('fallback_reemplaza_video', v)}
                checkedChildren="Sí" unCheckedChildren="No"
              />
              <Text strong>Reemplazar miniaturas de YouTube</Text>
            </Space>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
              {config.fallback_reemplaza_video
                ? 'Sin foto → esta imagen, aunque el animal tenga video.'
                : 'Sin foto → miniatura del video → esta imagen.'}
            </Text>
          </div>
          <Divider />
        </>
      )}

      {/* POSICIÓN FILTRO */}
      {!ocultar.includes('filtro_posicion') && (
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 8 }}>Posición del Filtro</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Ubicación de la barra de filtros
        </Text>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <ImageOption
              value="izquierda"
              currentValue={config.filtro_posicion}
              src={`${API_HOST}/static/uploads/filtros/filtro-izq.webp`}
              alt="Filtro izquierda"
              onClick={(v) => onChange('filtro_posicion', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Izquierda
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <ImageOption
              value="derecha"
              currentValue={config.filtro_posicion}
              src={`${API_HOST}/static/uploads/filtros/filtro-der.webp`}
              alt="Filtro derecha"
              onClick={(v) => onChange('filtro_posicion', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Derecha
            </Text>
          </Col>
        </Row>
      </div>
      )}

      <Divider />

      {/* COLUMNAS FOOTER */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5} style={{ marginBottom: 8 }}>Columnas del Footer</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Estructura del pie de página
        </Text>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <ImageOption
              value="3"
              currentValue={config.columnas_footer}
              src={`${API_HOST}/static/uploads/footer/tres-columns-footer.webp`}
              alt="Footer 3 columnas"
              onClick={(v) => onChange('columnas_footer', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              3 Columnas
            </Text>
          </Col>
          <Col xs={24} sm={12}>
            <ImageOption
              value="4"
              currentValue={config.columnas_footer}
              src={`${API_HOST}/static/uploads/footer/cuatro-columns-footer.webp`}
              alt="Footer 4 columnas"
              onClick={(v) => onChange('columnas_footer', v)}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
              4 Columnas
            </Text>
          </Col>
        </Row>
      </div>

      {/* FICHA DEL ANIMAL — genealogía, DEP's y Corral/Lote. Oculto en el
          catálogo de genética (`ocultar`): esa pantalla tiene su propia
          presentación fija (barra de percentil, `AnimalCatalogoPage.jsx`) y no
          lee estas claves — mostrarlas ahí configuraría algo que nunca se
          dibuja. A propósito, Corral/Lote cae con la sección entera: su
          `ocultar` propio sólo sirve para sacarlo DENTRO de una pantalla que
          sí muestra las otras dos. */}
      {!ocultar.includes('genealogia_vista') && !ocultar.includes('deps_vista') && (
        <>
          <Divider />
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ marginBottom: 8 }}>Ficha del animal</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Cómo se leen la genealogía y los DEP's en la ficha pública de cada animal, y cómo llamás al grupo de venta
            </Text>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>Genealogía</Text>
            <Radio.Group
              value={config.genealogia_vista || 'arbol'}
              onChange={(e) => onChange('genealogia_vista', e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              {Object.entries(GENEALOGIA_VISTA_INFO).map(([clave, info]) => (
                <Radio.Button value={clave} key={clave}>{info.label}</Radio.Button>
              ))}
            </Radio.Group>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, marginBottom: 20, fontSize: 12 }}>
              {(GENEALOGIA_VISTA_INFO[config.genealogia_vista] || GENEALOGIA_VISTA_INFO.arbol).desc}
            </Text>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>DEP's</Text>
            <Radio.Group
              value={config.deps_vista || 'tabla'}
              onChange={(e) => onChange('deps_vista', e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              {Object.entries(DEPS_VISTA_INFO).map(([clave, info]) => (
                <Radio.Button value={clave} key={clave}>{info.label}</Radio.Button>
              ))}
            </Radio.Group>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, marginBottom: 20, fontSize: 12 }}>
              {(DEPS_VISTA_INFO[config.deps_vista] || DEPS_VISTA_INFO.tabla).desc}
            </Text>

            {/* CORRAL O LOTE. Va acá, con las otras dos elecciones de "cómo se
                lee la ficha" (pedido del usuario, 2026-10-15), y no arriba de
                todo. Es una elección entre dos nombres, no un sí/no: por eso un
                Radio y no un Switch. La palabra aparece en todas las páginas
                del micrositio: tarjetas, hero de la ficha, lotes agrupados y el
                texto de compartir. */}
            {!ocultar.includes('termino_lote') && (
              <>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>¿Corral o Lote?</Text>
                <Radio.Group
                  value={config.termino_lote || 'corral'}
                  onChange={(e) => onChange('termino_lote', e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="corral">Corral</Radio.Button>
                  <Radio.Button value="lote">Lote</Radio.Button>
                </Radio.Group>
                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                  Cómo llamás al grupo de animales que se vende junto. El micrositio usa esa palabra en todos lados: «{config.termino_lote === 'lote' ? 'Lote' : 'Corral'} 43 · 3 cabezas».
                </Text>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EstructuraTab;
